// src/components/superAdmin/SuperAdminDashboard.tsx

import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OrganizationSummary, PlatformMetrics } from '../../../../interfaces/superAdmin/SuperAdmin';
import { getAllOrganizations, getPlatformMetrics } from '../../../../services/superAdminServices/SuperAdminDashboardService';
import { useAuth } from '../../../../firebase/AuthProvider';
import { Search, Edit2, Users, Building, CheckCircle2, Copy, ArrowRight, BookOpen, Settings, Plus, X } from 'lucide-react';
import { BrandLoader } from '../../../ui/Logo/BrandLoader';
import { ROUTES } from '../../../../Data/Routes/Routes';
import './SuperAdminDashboard.css';
import { createOrganization } from '../../../../services/superAdminServices/SuperAdminOrganizationService';
import { toast } from 'react-toastify';

const SuperAdminDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
    const [orgs, setOrgs] = useState<OrganizationSummary[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [totalRecords, setTotalRecords] = useState(0);

    // Pagination state
    const [page, setPage] = useState(1);
    const [pageSize, ] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [showCreateOrganization, setShowCreateOrganization] = useState(false);
    const [creatingOrganization, setCreatingOrganization] = useState(false);
    const [newOrganization, setNewOrganization] = useState({ name: "", abbreviation: "" });
    const createOrganizationDialogRef = useRef<HTMLFormElement>(null);
    const createOrganizationNameRef = useRef<HTMLInputElement>(null);
    const createOrganizationOpenerRef = useRef<HTMLElement | null>(null);

    const openCreateOrganization = () => {
        createOrganizationOpenerRef.current = document.activeElement as HTMLElement | null;
        setShowCreateOrganization(true);
    };

    const closeCreateOrganization = () => setShowCreateOrganization(false);

    useEffect(() => {
        if (!showCreateOrganization) return;

        const focusFrame = window.requestAnimationFrame(() => createOrganizationNameRef.current?.focus());
        const handleDialogKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                closeCreateOrganization();
                return;
            }

            if (event.key !== "Tab" || !createOrganizationDialogRef.current) return;
            const focusableElements = Array.from(
                createOrganizationDialogRef.current.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                ),
            );
            if (!focusableElements.length) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (event.shiftKey && (document.activeElement === firstElement || !createOrganizationDialogRef.current.contains(document.activeElement))) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && (document.activeElement === lastElement || !createOrganizationDialogRef.current.contains(document.activeElement))) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        document.addEventListener("keydown", handleDialogKeyDown);
        return () => {
            window.cancelAnimationFrame(focusFrame);
            document.removeEventListener("keydown", handleDialogKeyDown);
            createOrganizationOpenerRef.current?.focus();
        };
    }, [showCreateOrganization]);

    const handleCreateOrganization = async (event: FormEvent) => {
        event.preventDefault();
        if (!currentUser) return;
        setCreatingOrganization(true);
        try {
            const authToken = await currentUser.getIdToken();
            const created = await createOrganization(authToken, newOrganization);
            toast.success(`${created.name} was created as a draft organization.`);
            navigate(`/superadmin/organizations/${created.id}`);
        } catch (reason) {
            toast.error(reason instanceof Error ? reason.message : "Unable to create the organization.");
        } finally {
            setCreatingOrganization(false);
        }
    };

    // Fetch Data
    useEffect(() => {
        const loadData = async () => {
            if (!currentUser) return;

            setLoading(true);
            const authToken = await currentUser.getIdToken();

            const [metricsData, orgData] = await Promise.all([
                getPlatformMetrics(authToken),
                getAllOrganizations(authToken, page, pageSize, searchTerm)
            ]);

            setMetrics(metricsData);
            if (orgData) {
                setOrgs(orgData.data);
                setTotalRecords(orgData.totalRecords);
            }
            setLoading(false);
        };

        // Simple debounce timeout to prevent spamming the API on every single keystroke
        const delayDebounceFn = setTimeout(() => {
            loadData();
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [page, pageSize, searchTerm, currentUser]);

    // Pagination Logic
    const totalPages = Math.ceil(totalRecords / pageSize);
    const handlePrev = () => { if (page > 1) setPage(page - 1); };
    const handleNext = () => { if (page < totalPages) setPage(page + 1); };

    // FIX: Only show full-page loader on absolute initial boot. 
    // This prevents the search input from wiping the dashboard UI.
    if (loading && !metrics) return <div className="super-admin-dashboard-theme flex justify-center items-center h-screen bg-gray-50">
        <BrandLoader />
    </div>

    return (
        <div className="super-admin-dashboard-theme p-4 w-full bg-gray-50 min-h-screen">
            <div className="super-admin-activity-entry"><button type="button" onClick={() => navigate('/superadmin/visibility-metrics')}><span>Product activity</span><ArrowRight size={16} aria-hidden="true" /></button></div>
            {/* 1. Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="super-admin-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center gap-4 transition-all hover:border-purple-200 hover:shadow-md">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-[#7B5CD6]">
                        <Users className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="mb-1"><h3 className="text-gray-500 text-sm font-bold uppercase tracking-wide mb-0">Total Active Users</h3></div>
                        <p className="text-2xl font-bold text-gray-900">{metrics?.totalActiveUsers ?? "-"}</p>
                    </div>
                </div>
                <div className="super-admin-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center gap-4 transition-all hover:border-blue-200 hover:shadow-md">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                        <Building className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="mb-1"><h3 className="text-gray-500 text-sm font-bold uppercase tracking-wide mb-0">Total Organizations</h3></div>
                        <p className="text-2xl font-bold text-gray-900">{metrics?.totalOrganizations ?? "-"}</p>
                    </div>
                </div>
                <div className="super-admin-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center gap-4 transition-all hover:border-emerald-200 hover:shadow-md">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                        <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="mb-1"><h3 className="text-gray-500 text-sm font-bold uppercase tracking-wide mb-0">Tasks Closed (Month)</h3></div>
                        <p className="text-2xl font-bold text-gray-900">{metrics?.tasksClosedThisMonth ?? "-"}</p>
                    </div>
                </div>
            </div>

            {/* 2. System Tools */}
            <div className="mb-4">
                <h2 className="text-lg font-bold text-gray-800 mb-0">System Tools</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Recurring Task Projections */}
                {/* <div
                    onClick={() => navigate('/superadmin/projections')}
                    className="super-admin-card super-admin-tool-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all cursor-pointer group"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 group-hover:bg-orange-200 transition-colors">
                            <Calendar className="w-5 h-5" />
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-orange-500 transition-colors" />
                    </div>
                    <div className="mb-1"><h3 className="font-bold text-gray-800 mb-0">Recurring Task Projections</h3></div>
                    <p className="text-sm text-gray-500">Visualize future recurring tasks, audit Cron schedules, and identify missed job runs.</p>
                </div> */}
                {/* Role & Blueprint Management */}
                <div
                    onClick={() => navigate('/superadmin/blueprints')}
                    className="super-admin-card super-admin-tool-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-[#7B5CD6] hover:shadow-md transition-all cursor-pointer group"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-[#7B5CD6] group-hover:bg-purple-200 transition-colors">
                            <Copy className="w-5 h-5" />
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#7B5CD6] transition-colors" />
                    </div>
                    <div className="mb-1"><h3 className="font-bold text-gray-800 mb-0">Blueprint Templates</h3></div>
                    <p className="text-sm text-gray-500">Manage global role templates (Blueprints) to streamline organization onboarding.</p>
                </div>
                {/* Global Wiki & FAQ Editor */}
                <div
                    onClick={() => navigate('/admin/faqs')}
                    className="super-admin-card super-admin-tool-card bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer group"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 group-hover:bg-emerald-200 transition-colors">
                            <BookOpen className="w-5 h-5" />
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-emerald-500 transition-colors" />
                    </div>
                    <div className="mb-1"><h3 className="font-bold text-gray-800 mb-0">Wiki & FAQ Editor</h3></div>
                    <p className="text-sm text-gray-500">Draft, edit, and publish global documentation and FAQs for all users.</p>
                </div>
            </div>

            {/* 3. Organizations Table */}
            <div className="super-admin-table-surface bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="super-admin-divider p-4 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4 bg-white">
                    <h2 className="text-lg font-bold text-gray-800 mb-0">Organizations</h2>
                    <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
                    <button onClick={openCreateOrganization} className="inline-flex items-center justify-center gap-2 rounded-md bg-[#7B5CD6] px-4 py-2 text-sm font-bold text-white hover:bg-[#684bbd] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7B5CD6] focus-visible:ring-offset-2">
                        <Plus className="h-4 w-4" /> Create organization
                    </button>
                    <div className="relative w-full md:w-64">
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                            <Search className="w-4 h-4" />
                        </span>
                        <input
                            type="text"
                            placeholder="Search Organization..."
                            className="super-admin-control w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6] text-sm outline-none transition-all shadow-sm"
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                        />
                    </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-separate border-spacing-0">
                        <thead className="super-admin-table-head bg-gray-50">
                            <tr>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">Name</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">Code</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">Active Users</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">Config Status</th>
                                <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan={5} className="p-8 text-center text-gray-400 font-medium">Loading...</td></tr>
                            ) : orgs.length > 0 ? (
                                orgs.map((org) => (
                                    <tr key={org.id} className="super-admin-table-row hover:bg-gray-50 transition-colors">
                                        <td className="p-4 font-semibold text-gray-900">{org.name}</td>
                                        <td className="p-4 text-gray-600 font-mono text-sm">{org.abbreviation}</td>
                                        <td className="p-4 text-gray-600 font-medium">{org.activeUserCount}</td>
                                        <td className="p-4">
                                            {org.hasCustomConfiguration ? (
                                                <span className="super-admin-badge super-admin-badge-custom inline-flex items-center px-2.5 py-0.5 rounded border border-purple-200 text-xs font-bold bg-purple-50 text-[#7B5CD6]">
                                                    Custom Rules
                                                </span>
                                            ) : (
                                                <span className="super-admin-badge inline-flex items-center px-2.5 py-0.5 rounded border border-gray-200 text-xs font-bold bg-gray-50 text-gray-500">
                                                    Default
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => navigate(`/superadmin/organizations/${org.id}`)}
                                                className="inline-flex items-center text-[#7B5CD6] hover:text-[#5e45a3] text-sm font-bold transition-colors mr-5"
                                                title={`Open ${org.name} organization workspace`}
                                            >
                                                <Settings className="w-4 h-4 mr-1.5" />
                                                Manage
                                            </button>
                                            <button
                                                onClick={() => navigate(`/superadmin/roles/${org.id}`)}
                                                className="inline-flex items-center text-gray-500 hover:text-[#7B5CD6] text-sm font-bold transition-colors mr-5"
                                                title="Manage Roles & Policies"
                                            >
                                                <Users className="w-4 h-4 mr-1.5" />
                                                Roles
                                            </button>
                                            <button
                                                onClick={() => navigate(`/superadmin/workflow/${org.id}`)}
                                                className="inline-flex items-center text-gray-500 hover:text-[#7B5CD6] text-sm font-bold transition-colors"
                                                title="Edit Workflow Rules"
                                            >
                                                <Edit2 className="w-4 h-4 mr-1.5" />
                                                Workflow
                                            </button>
                                            <button
                                                onClick={() => navigate(ROUTES.BillingDetailsByOrgId(org.id))}
                                                className="inline-flex items-center text-gray-500 hover:text-[#7B5CD6] text-sm font-bold transition-colors"
                                                title="Edit Workflow Rules"
                                            >
                                                <Edit2 className="w-4 h-4 mr-1.5" />
                                                Billing
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={5} className="p-8 text-center text-gray-400 font-medium">No organizations found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="super-admin-divider p-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
                    <div className="text-xs font-medium text-gray-500">
                        Page {page} of {totalPages || 1} • Total {totalRecords} Records
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handlePrev}
                            disabled={page === 1}
                            className="super-admin-page-button px-3 py-1.5 text-xs font-bold rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                        >
                            Previous
                        </button>
                        <button
                            onClick={handleNext}
                            disabled={page >= totalPages}
                            className="super-admin-page-button px-3 py-1.5 text-xs font-bold rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
            {showCreateOrganization && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <form ref={createOrganizationDialogRef} role="dialog" aria-modal="true" aria-labelledby="create-organization-title" onSubmit={handleCreateOrganization} className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
                        <div className="flex items-start justify-between gap-4"><div><h2 id="create-organization-title" className="text-xl font-bold text-gray-900">Create organization</h2><p className="mt-1 text-sm text-gray-500">Creates a tenant-scoped draft. Add the owner from its workspace.</p></div><button type="button" aria-label="Close" onClick={closeCreateOrganization} className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7B5CD6] focus-visible:ring-offset-2"><X className="h-5 w-5" /></button></div>
                        <label className="mt-5 block text-sm font-semibold text-gray-700">Organization name<input ref={createOrganizationNameRef} autoFocus required value={newOrganization.name} onChange={(event) => setNewOrganization({ ...newOrganization, name: event.target.value })} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7B5CD6]" /></label>
                        <label className="mt-4 block text-sm font-semibold text-gray-700">Abbreviation<input required maxLength={12} value={newOrganization.abbreviation} onChange={(event) => setNewOrganization({ ...newOrganization, abbreviation: event.target.value.toUpperCase() })} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7B5CD6]" /></label>
                        <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={closeCreateOrganization} className="rounded-md border border-gray-300 px-4 py-2 font-semibold text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7B5CD6] focus-visible:ring-offset-2">Cancel</button><button disabled={creatingOrganization} className="rounded-md bg-[#7B5CD6] px-4 py-2 font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7B5CD6] focus-visible:ring-offset-2 disabled:opacity-50">{creatingOrganization ? "Creating…" : "Create draft"}</button></div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default SuperAdminDashboard;
